import { PlanningTask } from '../../services/strategicPlanningApi';
import { formatDateBr } from '../../utils/formatDateBr';
import { PLANNING_TASK_STATUSES } from '../../types/PlanningTaskStatus';

type PlanningTaskKanbanBoardProps = {
  tasks: PlanningTask[];
  onSelect: (task: PlanningTask) => void;
  ownerNamesById?: Record<string, string>;
};

function PriorityIcon({ priority }: { priority?: string | null }) {
  if (priority === 'High' || priority === 'Critical') {
    return (
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700"
        title={priority === 'Critical' ? 'Prioridade crítica' : 'Prioridade alta'}
        aria-label={priority === 'Critical' ? 'Prioridade crítica' : 'Prioridade alta'}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden>
          <path d="M10 2.5 17 16H3L10 2.5Z" />
        </svg>
      </span>
    );
  }

  if (priority === 'Medium') {
    return (
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"
        title="Prioridade média"
        aria-label="Prioridade média"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden>
          <path d="M4 9h12v2H4V9Z" />
        </svg>
      </span>
    );
  }

  return null;
}

export function PlanningTaskKanbanBoard({
  tasks,
  onSelect,
  ownerNamesById = {},
}: PlanningTaskKanbanBoardProps) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {PLANNING_TASK_STATUSES.map((column) => {
        const columnItems = tasks.filter((task) => task.status === column.id);

        return (
          <div
            key={column.id}
            className={`flex min-w-[260px] max-w-[300px] flex-1 flex-col rounded-xl border ${column.columnClass}`}
          >
            <div className={`rounded-t-xl px-3 py-2 ${column.headerClass}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{column.label}</span>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {columnItems.length}
                </span>
              </div>
            </div>

            <div className="flex min-h-[140px] flex-1 flex-col gap-2 p-2">
              {columnItems.length === 0 ? (
                <p className="px-2 py-4 text-center text-xs text-secondary-500">Nenhuma tarefa</p>
              ) : (
                columnItems.map((task) => {
                  const ownerName = task.ownerUserId
                    ? ownerNamesById[task.ownerUserId] ?? task.ownerUserName
                    : null;

                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => onSelect(task)}
                      className={`w-full !rounded-l-none !rounded-r-xl border border-l-4 border-secondary-200 bg-white p-3 text-left shadow-sm transition hover:shadow-md ${column.cardAccentClass}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="line-clamp-2 min-w-0 flex-1 font-semibold text-secondary-900">
                          {task.title}
                        </p>
                        <PriorityIcon priority={task.priority} />
                      </div>

                      <dl className="mt-2 space-y-1 text-xs text-secondary-600">
                        <div className="flex gap-1">
                          <dt className="shrink-0 text-secondary-500">Responsável:</dt>
                          <dd className="truncate font-medium text-secondary-800">
                            {ownerName?.trim() || 'Não atribuído'}
                          </dd>
                        </div>
                        <div className="flex gap-1">
                          <dt className="shrink-0 text-secondary-500">Prazo:</dt>
                          <dd className="font-medium text-secondary-800">
                            {task.plannedEndDate ? formatDateBr(task.plannedEndDate) : '—'}
                          </dd>
                        </div>
                      </dl>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
